import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { CircularProgress } from "@mui/material";
import { GridPagination } from "../../../../../Components/Molecules/GridHistory";
import { fetchPixCharges } from "../../../../../Services/pixChargesApi";
import { paginateList } from "../../../Extract/extractFilters";
import { PixChargesFilterBar } from "../PixChargesFilterBar";
import { PixChargeDetailDrawer } from "./PixChargeDetailDrawer";
import { PixChargesList } from "./PixChargesList";
import {
  EMPTY_PIX_CHARGES_FILTERS,
  applyPixChargesClientFilters,
} from "../pixChargesFilters";

const DEFAULT_ROWS_PER_PAGE = 10;

export const PixChargesPanel = () => {
  const [filters, setFilters] = useState(EMPTY_PIX_CHARGES_FILTERS);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadCharges = async () => {
    setLoading(true);
    const result = await fetchPixCharges();
    setLoading(false);
    if (result.success) {
      setItems(result.data?.items ?? []);
    }
  };

  useEffect(() => {
    loadCharges();
  }, []);

  const filteredItems = useMemo(
    () => applyPixChargesClientFilters(items, filters),
    [items, filters],
  );

  const paginatedItems = useMemo(
    () => paginateList(filteredItems, page, rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredItems.length / rowsPerPage) || 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredItems.length, rowsPerPage, page]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const handleSelectCharge = (charge) => {
    setSelectedCharge(charge);
    setDetailOpen(true);
  };

  const handleChargeUpdated = (updated) => {
    setItems((prev) =>
      prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
    );
    setSelectedCharge((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
  };

  return (
    <section>
      <h2 className="mb-3 text-sm font-bold text-brand-dark dark:text-gray-100">
        {intl.get("pix.charges.title")}
      </h2>

      <PixChargesFilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        filteredItems={filteredItems}
      />

      <div className="mt-4">
        {loading ? (
          <div className="app-card flex items-center justify-center py-12">
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            <PixChargesList
              items={paginatedItems}
              onSelectCharge={handleSelectCharge}
              footer={(
                <>
                  <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500 dark:border-brand-dark-border dark:text-gray-400">
                    {intl.get("pix.charges.loadedCount", { count: filteredItems.length })}
                  </p>
                  <GridPagination
                    total={filteredItems.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    currentPageCount={paginatedItems.length}
                    onPageChange={setPage}
                    onRowsPerPageChange={(value) => {
                      setRowsPerPage(value);
                      setPage(1);
                    }}
                    itemLabel={intl.get("pix.charges.itemLabel")}
                  />
                </>
              )}
            />
          </>
        )}
      </div>

      <PixChargeDetailDrawer
        open={detailOpen}
        charge={selectedCharge}
        onClose={() => {
          setDetailOpen(false);
          setSelectedCharge(null);
        }}
        onChargeUpdated={handleChargeUpdated}
      />
    </section>
  );
};
